import {adgroupLevelLabel} from '../../utils/channelLevelLabels';
import {isAutomatic, isNoneRule, ruleFromPayload} from './attributionModel';

/**
 * Los textos de la regla de cruce que salen de combinar datos. Reciben el `t` de quien dibuja, así se
 * testean sin React (patrón `agency/connections_v2/platformsText.js`).
 *
 * Lo que se interpola es dato (el nombre de una plataforma como «Display & Video 360», el de un
 * conjunto de anuncios) y i18next escapa `/ & < >` a entidades si no se le pide lo contrario: por eso
 * `RAW` va en cada uno.
 */

export const RAW = {escapeValue: false};

// Espacio no separable: «88 %» no se parte al final de la línea (como en `GA4FiltersPreview`).
const NBSP = ' ';

/** «Término (utm_term)», «Contenido (utm_content)» o «No cruzar». */
export function paramLabel(t, param) {
    return param ? t(`ga4_attribution.param.${param}`) : t('ga4_attribution.param.none');
}

/** «por nombre» / «por ID», para dentro de una frase. */
export function matchInlineText(t, matchBy) {
    return t(`ga4_attribution.match_inline.${matchBy === 'id' ? 'id' : 'name'}`);
}

/** La primera letra en mayúscula: los resúmenes arrancan con el rótulo del nivel, que va en minúscula. */
export function capitalizeFirst(text) {
    const value = String(text || '');
    return value ? value.charAt(0).toLocaleUpperCase() + value.slice(1) : value;
}

/**
 * La regla en una línea: «Conjunto de anuncios: Término (utm_term) · anuncio: Contenido (utm_content) ·
 * por nombre». Google tiene su propia frase (se cruza solo, por la vinculación con Google Ads).
 *
 * Con `compact`, la forma del bloque de la conexión de GA4 (UD-10), que va al lado del nombre de la
 * plataforma: «Conjunto de anuncios en Término (utm_term) · anuncio en Contenido (utm_content) · por
 * nombre» / «No cruza». La línea y el modal de la cuenta siguen con la forma de siempre.
 */
export function ruleSummaryText(t, channel, payload, {compact = false} = {}) {
    if (isAutomatic(channel, payload)) return t('ga4_attribution.summary.google_auto');
    const prefix = compact ? 'ga4_attribution.summary.compact_' : 'ga4_attribution.summary.';
    const rule = ruleFromPayload(payload);
    if (isNoneRule(rule)) return t(`${prefix}none`);
    const values = {
        level: adgroupLevelLabel(t, channel),
        adgroupParam: paramLabel(t, rule.adgroup_param),
        adParam: paramLabel(t, rule.ad_param),
        match: matchInlineText(t, rule.match_by),
        interpolation: RAW,
    };
    let key = `${prefix}both`;
    if (!rule.ad_param) key = `${prefix}adgroup_only`;
    else if (!rule.adgroup_param) key = `${prefix}ad_only`;
    return capitalizeFirst(t(key, values));
}

/**
 * Un porcentaje que viaja como fracción (`0.88`) en el formato de la pantalla: «88 %» en español
 * (es-AR, con espacio no separable) y «88%» en inglés. Con menos de 10 lleva un decimal, como la vista
 * previa de los filtros GA4. Sin dato, `null`.
 */
export function percentText(fraction, language) {
    if (fraction === null || fraction === undefined || Number.isNaN(Number(fraction))) return null;
    const value = Math.min(Math.max(Number(fraction) * 100, 0), 100);
    const english = String(language || 'es').startsWith('en');
    const digits = value > 0 && value < 10 ? 1 : 0;
    const number = value.toLocaleString(english ? 'en-US' : 'es-AR', {
        maximumFractionDigits: digits, minimumFractionDigits: 0,
    });
    return english ? `${number}%` : `${number}${NBSP}%`;
}

/** Un entero en el formato de la pantalla («1.240» en español, «1,240» en inglés). */
export function numberText(value, language) {
    const english = String(language || 'es').startsWith('en');
    return Math.round(Number(value) || 0).toLocaleString(english ? 'en-US' : 'es-AR');
}
